// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from '@lfx/shared/components/button';

export interface DropdownMenuItem {
  id: string;
  label: string;
  icon?: string; // FontAwesome class (e.g., 'far fa-edit')
  svgIcon?: string; // SVG asset path (e.g., 'assets/img/svgs/layer-minus.svg')
  action: string; // Action identifier
  variant?: 'default' | 'danger' | 'warning'; // Styling variant
  disabled?: boolean;
}

export interface DropdownMenuClickEvent {
  item: DropdownMenuItem;
  action: string;
}

/**
 * Reusable Dropdown Menu Component
 *
 * A flexible dropdown menu with icon trigger and configurable menu items.
 * Supports both FontAwesome and SVG asset icons for the trigger button.
 *
 * @example
 * ```html
 * <!-- FontAwesome trigger icon -->
 * <lfx-dropdown-menu
 *   triggerIcon="fas fa-ellipsis-v"
 *   triggerAriaLabel="Actions"
 *   [items]="actionItems"
 *   (itemClicked)="handleMenuClick($event)">
 * </lfx-dropdown-menu>
 *
 * <!-- SVG asset trigger icon -->
 * <lfx-dropdown-menu
 *   triggerSvgIcon="assets/img/svgs/settings.svg"
 *   triggerAriaLabel="Settings"
 *   [items]="settingsItems"
 *   (itemClicked)="handleSettingsClick($event)">
 * </lfx-dropdown-menu>
 * ```
 */
@Component({
  selector: 'lfx-dropdown-menu',
  imports: [CommonModule, NgbDropdownModule, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="dropdown-menu-wrapper" ngbDropdown>
      <!-- Trigger Button -->
      <lfx-button
        variant="icon"
        [size]="triggerSize()"
        [icon]="triggerIcon()"
        [svgIcon]="triggerSvgIcon()"
        [iconOnly]="true"
        [ariaLabel]="triggerAriaLabel()"
        [disabled]="disabled()"
        ngbDropdownToggle>
      </lfx-button>

      <!-- Dropdown Menu -->
      <div class="lf-dropdown-menu" ngbDropdownMenu>
        <div class="lf-dropdown-content">
          @for (item of items(); track item.id) {
            <button
              class="lf-dropdown-item with-icon"
              [class.danger-item]="item.variant === 'danger'"
              [class.warning-item]="item.variant === 'warning'"
              [disabled]="item.disabled"
              (click)="onItemClick(item)"
              ngbDropdownItem>
              @if (item.svgIcon) {
                <img [src]="item.svgIcon" [alt]="item.label" class="dropdown-svg-icon">
              } @else if (item.icon) {
                <i [class]="item.icon"></i>
              }
              <span class="item-text">{{ item.label }}</span>
            </button>
          }
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./dropdown-menu.component.scss']
})
export class DropdownMenuComponent {
  // =============================================================================
  // INPUTS - Trigger Configuration
  // =============================================================================

  /** FontAwesome icon class for trigger button (e.g., 'fas fa-ellipsis-v') */
  readonly triggerIcon = input<string>('');

  /** SVG asset path for trigger button (e.g., 'assets/img/svgs/settings.svg') */
  readonly triggerSvgIcon = input<string>('');

  /** ARIA label for trigger button accessibility */
  readonly triggerAriaLabel = input<string>('Menu');

  /** Size of trigger button */
  readonly triggerSize = input<'large' | 'small'>('large');

  /** Whether the dropdown is disabled */
  readonly disabled = input<boolean>(false);

  // =============================================================================
  // INPUTS - Menu Configuration
  // =============================================================================

  /** Array of dropdown menu items */
  readonly items = input<DropdownMenuItem[]>([]);

  // =============================================================================
  // OUTPUTS
  // =============================================================================

  /** Emitted when a dropdown item is clicked */
  readonly itemClicked = output<DropdownMenuClickEvent>();

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  protected onItemClick(item: DropdownMenuItem): void {
    if (item.disabled) {
      return;
    }

    this.itemClicked.emit({
      item,
      action: item.action
    });
  }
}
